/*
  hfb-motion.js — Hobby Lobbiest bespoke motion. origin: hfb. Dependency-free.
  Motion tier Signature (layout.md §3, UX-001):
    - reveal chokepoint (headings + grid stagger)   [universal floor]
    - rotating card carousel (the named signature)  [one instance sitewide]
    - PDP hover-magnifier + lightbox zoom
    - count-up (restrained)
  All respect prefers-reduced-motion.
*/
(function () {
  'use strict';
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal chokepoint ---------- */
  function initReveal() {
    var els = document.querySelectorAll('.hfb-reveal, .hfb-reveal-stagger');
    if (!els.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        if (el.classList.contains('hfb-reveal-stagger')) {
          Array.prototype.forEach.call(el.children, function (child, i) {
            child.style.transitionDelay = (i * 40) + 'ms';
          });
          // drop the stagger once revealed, or hover transitions on later children lag behind
          setTimeout(function () {
            Array.prototype.forEach.call(el.children, function (child) { child.style.transitionDelay = ''; });
          }, el.children.length * 40 + 700);
        }
        el.classList.add('is-in');
        io.unobserve(el);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Count-up (restrained) ---------- */
  function initCountUp() {
    var nums = document.querySelectorAll('[data-hfb-countup]');
    if (!nums.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      nums.forEach(function (n) { n.textContent = n.getAttribute('data-hfb-countup'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var n = e.target, target = parseFloat(n.getAttribute('data-hfb-countup')) || 0;
        var suffix = n.getAttribute('data-hfb-suffix') || '', start = null, dur = 1100;
        function step(t) {
          if (!start) start = t;
          var p = Math.min((t - start) / dur, 1);
          n.textContent = Math.round(target * (1 - Math.pow(1 - p, 3))) + suffix;
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
        io.unobserve(n);
      });
    }, { threshold: 0.5 });
    nums.forEach(function (n) { io.observe(n); });
  }

  /* ---------- Rotating card carousel (3D coverflow) ---------- */
  function initCarousel(root) {
    var track = root.querySelector('[data-hfb-carousel-track]');
    if (!track) return;
    var slides = Array.prototype.slice.call(track.children);
    if (slides.length < 2) { slides.forEach(function (s) { s.classList.add('is-active'); }); return; }
    var active = Math.floor(slides.length / 2);
    var timer = null, DELAY = 3600;
    var n = slides.length;
    var prevOffsets = [];

    function layout() {
      // side-card spread comes from CSS so a narrower layout (split hero) can tighten the fan
      var spread = parseFloat(getComputedStyle(root).getPropertyValue('--hfb-carousel-spread')) || 46;
      slides.forEach(function (s, i) {
        // circular offset so the ring loops: the card past one end re-enters at the other
        var offset = i - active;
        if (offset > n / 2) offset -= n;
        else if (offset < -n / 2) offset += n;
        // a card wrapping around jumps across the ring; move it instantly, not as a fly-through
        var wrapped = prevOffsets[i] !== undefined && Math.abs(offset - prevOffsets[i]) > 1;
        prevOffsets[i] = offset;
        if (wrapped) s.style.transition = 'none';
        var abs = Math.abs(offset);
        var depth = abs > 2 ? 3 : abs;
        s.style.transform =
          'translateX(' + (offset * spread) + '%) ' +
          'translateZ(' + (-depth * 130) + 'px) ' +
          'rotateY(' + (offset * -22) + 'deg) ' +
          'scale(' + (offset === 0 ? 1 : 0.86) + ')';
        s.style.zIndex = String(100 - depth);
        s.style.opacity = abs > 2 ? '0' : (offset === 0 ? '1' : '0.62');
        s.classList.toggle('is-active', offset === 0);
        s.setAttribute('aria-hidden', offset === 0 ? 'false' : 'true');
        if (wrapped) { void s.offsetWidth; s.style.transition = ''; }
      });
      if (typeof syncTab === 'function') syncTab();
    }
    function go(dir) { active = (active + dir + slides.length) % slides.length; layout(); }
    function play() { if (reduce) return; stop(); timer = setInterval(function () { go(1); }, DELAY); }
    function stop() { if (timer) clearInterval(timer); timer = null; }

    var prev = root.querySelector('[data-hfb-carousel-prev]');
    var next = root.querySelector('[data-hfb-carousel-next]');
    if (prev) prev.addEventListener('click', function () { go(-1); play(); });
    if (next) next.addEventListener('click', function () { go(1); play(); });

    // drag / swipe
    var startX = null, dragged = false;
    function down(x) { startX = x; dragged = false; stop(); }
    function up(x) {
      if (startX === null) return;
      var d = x - startX;
      if (Math.abs(d) > 40) { dragged = true; go(d < 0 ? 1 : -1); }
      startX = null; play();
    }
    // the cards are linked images: block the browser's native image/link drag, which would
    // swallow the mouseup and leave the grab cursor doing nothing
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });
    track.addEventListener('mousedown', function (e) { if (e.button === 0) { e.preventDefault(); down(e.clientX); } });
    window.addEventListener('mouseup', function (e) { up(e.clientX); });
    track.addEventListener('touchstart', function (e) { down(e.touches[0].clientX); }, { passive: true });
    track.addEventListener('touchend', function (e) { up((e.changedTouches[0] || {}).clientX || startX); });
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', play);

    // click a side card to bring it forward; the front card's link opens its product
    slides.forEach(function (s, i) {
      s.addEventListener('click', function (e) {
        if (dragged) { e.preventDefault(); dragged = false; return; }
        if (i !== active) { e.preventDefault(); active = i; layout(); play(); }
      });
    });
    // a link in a hidden side card must not take keyboard focus
    function syncTab() {
      slides.forEach(function (s, i) {
        var a = s.querySelector('a');
        if (a) { if (i === active) a.removeAttribute('tabindex'); else a.setAttribute('tabindex', '-1'); }
      });
    }

    layout();
    play();
    // the spread changes at the split-hero breakpoint
    window.addEventListener('resize', layout);
  }

  /* ---------- Hero background crossfade ---------- */
  function initHeroBg(root) {
    if (root.hfbTimer) clearInterval(root.hfbTimer);
    var imgs = root.querySelectorAll('.hfb-hero__bg-img');
    if (imgs.length < 2 || reduce) return;
    var current = 0;
    root.hfbTimer = setInterval(function () {
      imgs[current].classList.remove('is-active');
      current = (current + 1) % imgs.length;
      imgs[current].classList.add('is-active');
    }, 8000);
  }

  /* ---------- Announcement bar message rotation ---------- */
  function initAnnounce(root) {
    if (root.hfbTimer) clearInterval(root.hfbTimer);
    var msgs = root.querySelectorAll('.hfb-announce__msg');
    if (msgs.length < 2 || reduce) return;
    var current = 0;
    root.hfbTimer = setInterval(function () {
      if (root.closest('.hfb-announce').matches(':hover')) return; // hold while being read
      var prev = msgs[current];
      prev.classList.remove('is-active');
      prev.classList.add('is-leaving');
      prev.setAttribute('aria-hidden', 'true');
      setTimeout(function () { prev.classList.remove('is-leaving'); }, 600);
      current = (current + 1) % msgs.length;
      msgs[current].classList.add('is-active');
      msgs[current].removeAttribute('aria-hidden');
    }, 4500);
  }

  /* ---------- Scroll parallax ([data-hfb-parallax] layers, e.g. newsletter bands) ---------- */
  // The layer drifts at 12% of the section's distance from the viewport center, clamped to the
  // 30% overhang the CSS gives it. Capture-phase listener: Horizon scrolls .page-wrapper on desktop.
  var parallaxBound = false;
  function updateParallax() {
    var vh = window.innerHeight;
    document.querySelectorAll('[data-hfb-parallax]').forEach(function (layer) {
      var r = layer.parentElement.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) return; // off screen
      var limit = r.height * 0.3;
      var y = ((r.top + r.height / 2) - vh / 2) * -0.12;
      y = Math.max(-limit, Math.min(limit, y));
      layer.style.transform = 'translate3d(0,' + y.toFixed(1) + 'px,0)';
    });
  }
  function initParallax() {
    if (reduce) return;
    if (!parallaxBound) {
      var ticking = false;
      var onScroll = function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () { updateParallax(); ticking = false; });
      };
      document.addEventListener('scroll', onScroll, { passive: true, capture: true });
      window.addEventListener('resize', onScroll);
      parallaxBound = true;
    }
    updateParallax();
  }

  /* ---------- Header shrink on scroll ---------- */
  // Hysteresis (on past 80px, off under 20px): shrinking the header shortens the page, which
  // would otherwise flip the state back and forth right at a single threshold.
  // Horizon (base.css) makes .page-wrapper the scroll container at >=990px and leaves the window
  // scrolling below that, so read both and listen in the capture phase (scroll doesn't bubble).
  var headerBound = false;
  function initHeaderShrink() {
    if (headerBound) return;
    var ticking = false;
    function update() {
      var header = document.querySelector('[data-hfb-header]');
      if (!header) return;
      var wrapper = document.querySelector('.page-wrapper');
      var y = Math.max(window.scrollY, wrapper ? wrapper.scrollTop : 0);
      if (y > 80) header.classList.add('is-scrolled');
      else if (y < 20) header.classList.remove('is-scrolled');
    }
    document.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { update(); ticking = false; });
    }, { passive: true, capture: true });
    headerBound = true;
    update();
  }

  /* ---------- PDP hover-magnifier + lightbox ---------- */
  function initZoom(root) {
    var main = root.querySelector('[data-hfb-zoom-main]');
    var thumbs = root.querySelectorAll('[data-hfb-zoom-thumb]');
    var lightbox = root.querySelector('[data-hfb-lightbox]');
    if (main) {
      // magnifier
      main.addEventListener('mousemove', function (e) {
        if (reduce) return;
        var img = main.querySelector('img'); if (!img) return;
        var r = main.getBoundingClientRect();
        var x = ((e.clientX - r.left) / r.width) * 100;
        var y = ((e.clientY - r.top) / r.height) * 100;
        img.style.transformOrigin = x + '% ' + y + '%';
        img.style.transform = 'scale(1.8)';
      });
      main.addEventListener('mouseleave', function () {
        var img = main.querySelector('img'); if (img) img.style.transform = '';
      });
      if (lightbox) {
        main.addEventListener('click', function () { lightbox.showModal ? lightbox.showModal() : (lightbox.open = true); });
      }
    }
    thumbs.forEach(function (t) {
      t.addEventListener('click', function () {
        var src = t.getAttribute('data-src');
        var img = main && main.querySelector('img');
        if (src && img) { img.src = src; }
        var lb = lightbox && lightbox.querySelector('img');
        if (src && lb) { lb.src = src; }
        thumbs.forEach(function (x) { x.classList.remove('is-active'); });
        t.classList.add('is-active');
      });
    });
    if (lightbox) {
      var close = lightbox.querySelector('[data-hfb-lightbox-close]');
      if (close) close.addEventListener('click', function () { lightbox.close ? lightbox.close() : (lightbox.open = false); });
      lightbox.addEventListener('click', function (e) { if (e.target === lightbox) { lightbox.close ? lightbox.close() : (lightbox.open = false); } });
    }
  }

  function init() {
    initReveal();
    initCountUp();
    document.querySelectorAll('[data-hfb-carousel]').forEach(initCarousel);
    document.querySelectorAll('[data-hfb-hero-bg]').forEach(initHeroBg);
    document.querySelectorAll('[data-hfb-announce]').forEach(initAnnounce);
    initHeaderShrink();
    initParallax();
    document.querySelectorAll('[data-hfb-pdp-gallery]').forEach(initZoom);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }
  // Re-init on Shopify section render (theme editor)
  document.addEventListener('shopify:section:load', init);
})();
